import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Upload, CheckCircle2, AlertTriangle, Loader2, Film, Image as ImageIcon, X, Download } from 'lucide-react';
import api from '../../services/api';

// Gestor de Tráfego — aba Criativos: biblioteca de imagens e vídeos para anúncios.
// Origem: pasta do Google Drive (Material_Portal/<empreendimento>/02 Feed, 03 Stories, 04 Vídeos e Reels, 06 Fotos)
// e o que já está na conta de anúncios (com o desempenho dos anúncios que usaram cada um).

const fmtBRL = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const erroMsg = (err) => err?.response?.data?.error || err?.message;
// baixa pelo link assinado do CRM (o servidor manda como anexo, com o nome da arte)
function baixar(c) {
  const a = document.createElement('a');
  a.href = c.download; a.rel = 'noopener'; a.download = '';
  document.body.appendChild(a); a.click(); a.remove();
}

const fmtTam = (b) => (!b ? '' : b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

function StatusMeta({ c }) {
  if (c.na_meta) return <span className="flex items-center gap-1 text-green-700"><CheckCircle2 size={12} /> Na Meta</span>;
  if (c.meta_status === 'enviando' || c.meta_status === 'processando') return <span className="flex items-center gap-1 text-blue-700"><Loader2 size={12} className="animate-spin" /> {c.meta_status === 'processando' ? 'Processando na Meta' : 'Enviando'}</span>;
  if (c.meta_status === 'erro') return <span className="flex items-center gap-1 text-red-700" title={c.meta_erro || ''}><AlertTriangle size={12} /> Erro ao enviar</span>;
  return <span className="text-gray-500">Só no Drive</span>;
}

function Cartao({ c, empreendimentos, onAbrir, marcado, onMarcar }) {
  const qc = useQueryClient();
  const enviar = useMutation({
    mutationFn: () => api.post(`/trafego/criativos/${c.id}/enviar`, {}, { timeout: 300000 }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trafego-criativos'] }),
    onError: (err) => { qc.invalidateQueries({ queryKey: ['trafego-criativos'] }); alert('Não enviou: ' + erroMsg(err)); },
  });
  const mudarEmp = useMutation({
    mutationFn: (empreendimento_id) => api.put(`/trafego/criativos/${c.id}/empreendimento`, { empreendimento_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trafego-criativos'] }),
  });
  const d = c.desempenho;
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
      <div className="relative">
      <label className="absolute bottom-2 left-2 z-10 bg-white/90 rounded px-1.5 py-0.5 flex items-center gap-1 text-[11px] cursor-pointer" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={marcado} onChange={() => onMarcar(c.id)} /> baixar
      </label>
      <button onClick={(e) => { e.stopPropagation(); baixar(c); }} title={c.origem === 'meta' && c.tipo === 'video' ? 'Baixar (a Meta só libera cópia em baixa resolução)' : 'Baixar o arquivo original'}
        className="absolute bottom-2 right-2 z-10 bg-white/90 hover:bg-white rounded p-1.5 text-gray-700"><Download size={14} /></button>
      <button onClick={() => onAbrir(c)} className="relative bg-gray-100 aspect-square w-full block">
        {c.miniatura ? <img src={c.miniatura} alt="" loading="lazy" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">sem prévia</div>}
        <span className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
          {c.tipo === 'video' ? <Film size={10} /> : <ImageIcon size={10} />}{c.tipo === 'video' ? `${c.duracao_s || '?'}s` : ''}{c.formato ? ` · ${c.formato}` : ''}
        </span>
        <span className="absolute top-2 right-2 bg-white/90 text-gray-700 text-[10px] px-1.5 py-0.5 rounded">{c.origem === 'drive' ? 'Drive' : 'Conta Meta'}</span>
      </button>
      </div>
      <div className="p-3 space-y-1.5 text-xs flex-1 flex flex-col">
        <div className="font-medium text-gray-800 break-words line-clamp-2" title={c.nome}>#{c.id} · {c.nome}</div>
        {c.secao && <div className="text-gray-400">{c.secao}{c.tamanho_bytes ? ` · ${fmtTam(c.tamanho_bytes)}` : ''}</div>}
        <select className="w-full border border-gray-200 rounded px-1 py-1 text-xs" value={c.empreendimento_id || ''} onChange={(e) => mudarEmp.mutate(e.target.value || null)}>
          <option value="">— sem empreendimento —</option>
          {empreendimentos.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        {d ? (
          <div className="bg-gray-50 rounded px-2 py-1 text-gray-600">
            {d.anuncios} anúncio(s) · {fmtBRL(d.investimento)} · {d.resultados} contato(s)
            {d.custo_resultado != null && <> · <strong className="text-gray-800">{fmtBRL(d.custo_resultado)}</strong>/contato</>}
            {d.ultimo_uso && <div className="text-gray-400">último uso {d.ultimo_uso.split('-').reverse().join('/')}</div>}
          </div>
        ) : <div className="text-gray-400">Ainda não rodou em anúncio</div>}
        <div className="mt-auto pt-1 flex items-center justify-between gap-2">
          <StatusMeta c={c} />
          {c.origem === 'drive' && !c.na_meta && c.meta_status !== 'processando' && (
            <button onClick={() => enviar.mutate()} disabled={enviar.isPending} className="flex items-center gap-1 bg-primary text-white px-2 py-1 rounded text-xs disabled:opacity-60">
              <Upload size={12} /> {enviar.isPending ? 'Enviando…' : 'Enviar p/ Meta'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Visualizar({ c, onFechar }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onFechar}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
          <div className="text-sm font-medium text-gray-800 truncate">#{c.id} · {c.nome}</div>
          <div className="flex items-center gap-2">
            <button onClick={() => baixar(c)} className="flex items-center gap-1 bg-primary text-white px-3 py-1 rounded text-xs"><Download size={13} /> {c.origem === 'meta' && c.tipo === 'video' ? 'Baixar (baixa resolução)' : 'Baixar original'}</button>
            <button onClick={onFechar} className="p-1"><X size={18} /></button>
          </div>
        </div>
        <div className="bg-gray-900 flex justify-center">
          {c.tipo === 'video' && c.arquivo
            ? <video src={c.arquivo} controls className="max-h-[70vh]" />
            : <img src={c.miniatura} alt="" className="max-h-[70vh] object-contain" />}
        </div>
        {c.tipo === 'video' && !c.arquivo && <p className="text-xs text-gray-500 p-3">Vídeo da conta de anúncios: aqui aparece só a capa. O download pelo CRM vem em resolução reduzida (cerca de 360p) — é o que a API da Meta libera. O arquivo original em alta sai pelo Gerenciador de Anúncios (Biblioteca de mídia › Baixar); guardando os originais na pasta do Drive, o CRM baixa sempre em alta.</p>}
      </div>
    </div>
  );
}

export default function Criativos() {
  const qc = useQueryClient();
  const [emp, setEmp] = useState('');
  const [tipo, setTipo] = useState('');
  const [origem, setOrigem] = useState('');
  const [usados, setUsados] = useState(false);
  const [aberto, setAberto] = useState(null);
  const [marcados, setMarcados] = useState([]);
  const marcar = (id) => setMarcados((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  const [baixando, setBaixando] = useState(false);
  // vários de uma vez: um download por arquivo, com intervalo (o navegador pode pedir para permitir downloads múltiplos)
  const baixarMarcados = async () => {
    const lista = (data?.criativos || []).filter((c) => marcados.includes(c.id));
    setBaixando(true);
    for (const c of lista) { baixar(c); await new Promise((r) => setTimeout(r, 1200)); }
    setBaixando(false); setMarcados([]);
  };
  const { data: empreendimentos = [] } = useQuery({ queryKey: ['trafego-emps'], queryFn: () => api.get('/trafego/empreendimentos').then((r) => r.data.data) });
  const { data, isLoading } = useQuery({
    queryKey: ['trafego-criativos', emp, tipo, origem, usados],
    queryFn: () => api.get('/trafego/criativos', { params: { empreendimento_id: emp || undefined, tipo: tipo || undefined, origem: origem || undefined, usados: usados ? '1' : undefined } }).then((r) => r.data.data),
    refetchInterval: (q) => ((q.state.data?.criativos || []).some((c) => c.meta_status === 'processando' || c.meta_status === 'enviando') ? 30000 : false),
  });
  const sincronizar = useMutation({
    mutationFn: () => api.post('/trafego/criativos/sincronizar', {}, { timeout: 300000 }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['trafego-criativos'] });
      const d = r.data.data;
      const partes = [];
      if (d.drive) partes.push(d.drive.erro ? `Drive: ${d.drive.erro}` : d.drive.pulado ? `Drive: ${d.drive.motivo}` : `Drive: ${d.drive.novos} novo(s)`);
      if (d.meta) partes.push(d.meta.erro ? `Meta: ${d.meta.erro}` : `Conta Meta: ${d.meta.imagens} imagem(ns), ${d.meta.videos} vídeo(s)`);
      alert(partes.join('\n'));
    },
    onError: (err) => alert(erroMsg(err)),
  });
  const lista = data?.criativos || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select className="border border-gray-200 rounded-lg text-sm px-3 py-2" value={emp} onChange={(e) => setEmp(e.target.value)}>
          <option value="">Todos os empreendimentos</option>
          {empreendimentos.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
          <option value="sem">Sem empreendimento</option>
        </select>
        <select className="border border-gray-200 rounded-lg text-sm px-3 py-2" value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="">Imagens e vídeos</option><option value="imagem">Só imagens</option><option value="video">Só vídeos</option>
        </select>
        <select className="border border-gray-200 rounded-lg text-sm px-3 py-2" value={origem} onChange={(e) => setOrigem(e.target.value)}>
          <option value="">Drive e conta Meta</option><option value="drive">Só do Drive</option><option value="meta">Só da conta Meta</option>
        </select>
        <label className="flex items-center gap-1 text-sm text-gray-600"><input type="checkbox" checked={usados} onChange={(e) => setUsados(e.target.checked)} /> Só os que já rodaram</label>
        {marcados.length > 0 && (
          <span className="flex items-center gap-2 ml-auto">
            <button onClick={baixarMarcados} disabled={baixando} className="flex items-center gap-2 bg-primary text-white px-3 py-2 rounded-lg text-sm disabled:opacity-60">
              <Download size={14} /> {baixando ? 'Baixando…' : `Baixar ${marcados.length} selecionado(s)`}
            </button>
            <button onClick={() => setMarcados([])} className="text-xs text-gray-500 underline">limpar</button>
          </span>
        )}
        <button onClick={() => sincronizar.mutate()} disabled={sincronizar.isPending} className={`flex items-center gap-2 border border-gray-200 bg-white px-3 py-2 rounded-lg text-sm disabled:opacity-60 ${marcados.length ? '' : 'ml-auto'}`}>
          <RefreshCw size={14} className={sincronizar.isPending ? 'animate-spin' : ''} /> {sincronizar.isPending ? 'Atualizando…' : 'Atualizar biblioteca'}
        </button>
      </div>
      <p className="text-xs text-gray-500">
        Para entrar aqui, ponha a arte na pasta do Google Drive <strong>Material_Portal › (empreendimento) › 02 Imagens para Feed, 03 Imagens para Stories, 04 Vídeos e Reels ou 06 Fotos e Perspectivas</strong>
        {data && !data.drive_configurado ? ' (o Drive não está ligado neste servidor)' : ''}. A biblioteca atualiza sozinha todo dia às 6h45. O Gestor IA usa estas artes nas campanhas que propõe, e as imagens do Drive vão para a Meta sozinhas quando a campanha é aprovada; vídeo precisa ser enviado antes (a Meta leva alguns minutos para processar).
      </p>
      {isLoading && <div className="text-gray-500 text-sm">Carregando…</div>}
      {!isLoading && !lista.length && <div className="bg-white rounded-xl border border-gray-100 p-6 text-sm text-gray-500">Nada com esse filtro. Clique em "Atualizar biblioteca" para ler o Drive e a conta de anúncios.</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {lista.map((c) => <Cartao key={c.id} c={c} empreendimentos={empreendimentos} onAbrir={setAberto} marcado={marcados.includes(c.id)} onMarcar={marcar} />)}
      </div>
      {aberto && <Visualizar c={aberto} onFechar={() => setAberto(null)} />}
    </div>
  );
}
